
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { format, isToday, isYesterday } from 'date-fns';
import { Star, Paperclip, Inbox, Send, Trash2, FileEdit, MailOpen, Mail, SearchX } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Email } from '@/services/emailService';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useEmail } from '@/context/EmailContext';
import { updateEmailMessage } from '@/services/api/email';
import { stripHtml } from '@/utils/emailHtml';

interface EmailListProps {
  emails: Email[];
  folder: string;
  selectedEmails: string[];
  onSelectEmail: (id: string, selected: boolean) => void;
  onSelectAll?: (selected: boolean) => void;
}

const getInitials = (name: string, address: string): string => {
  const source = (name || address || '?').trim();
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

const EmailList: React.FC<EmailListProps> = ({
  emails,
  folder,
  selectedEmails,
  onSelectEmail,
  onSelectAll
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('q') || '';
  const { markAsRead, markAsUnread, moveToTrash } = useEmail();
  // Local star overrides keyed by email id; fall back to the server value.
  const [starOverrides, setStarOverrides] = useState<Record<string, boolean>>({});

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  const handleEmailClick = (id: string, e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('.email-actions')) {
      return;
    }
    navigate(`/app/email/${folder}/${id}`);
  };

  const isStarred = (email: Email) =>
    starOverrides[email.id] ?? email.starred ?? false;

  const handleStarToggle = async (email: Email, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !isStarred(email);
    // Optimistic
    setStarOverrides(prev => ({ ...prev, [email.id]: next }));
    try {
      await updateEmailMessage(email.id, { is_starred: next });
    } catch (error) {
      // Revert on failure
      setStarOverrides(prev => ({ ...prev, [email.id]: !next }));
    }
  };

  const handleMarkRead = (email: Email, e: React.MouseEvent) => {
    e.stopPropagation();
    if (email.read) {
      markAsUnread(email.id).catch(() => {});
    } else {
      markAsRead(email.id).catch(() => {});
    }
  };

  const handleTrash = (email: Email, e: React.MouseEvent) => {
    e.stopPropagation();
    moveToTrash(email.id).catch(() => {});
  };

  const allSelected = emails.length > 0 && selectedEmails.length === emails.length;

  if (emails.length === 0) {
    if (searchQuery) {
      return (
        <div className="flex flex-col items-center justify-center h-full p-12 text-center bg-background">
          <div className="w-24 h-24 mb-6 rounded-full bg-muted flex items-center justify-center">
            <SearchX className="h-8 w-8 text-muted-foreground" />
          </div>
          <h3 className="text-xl font-medium text-foreground mb-2">No matches for "{searchQuery}"</h3>
          <p className="text-muted-foreground max-w-md">
            Try different keywords, or clear the search to see all mail in {folder}.
          </p>
        </div>
      );
    }
    return (
      <div className="flex flex-col items-center justify-center h-full p-12 text-center bg-background">
        <div className="w-24 h-24 mb-6 rounded-full bg-muted flex items-center justify-center">
          {folder === 'inbox' && <Inbox className="h-8 w-8 text-muted-foreground" />}
          {folder === 'sent' && <Send className="h-8 w-8 text-muted-foreground" />}
          {folder === 'drafts' && <FileEdit className="h-8 w-8 text-muted-foreground" />}
          {folder === 'trash' && <Trash2 className="h-8 w-8 text-muted-foreground" />}
        </div>
        <h3 className="text-xl font-medium text-foreground mb-2">
          {folder === 'inbox' && "Your inbox is empty"}
          {folder === 'sent' && "No sent emails"}
          {folder === 'drafts' && "No drafts"}
          {folder === 'trash' && "Trash is empty"}
        </h3>
        <p className="text-muted-foreground max-w-md">
          {folder === 'inbox' && "When you receive emails, they'll appear here."}
          {folder === 'sent' && "Emails you send will appear here."}
          {folder === 'drafts' && "Drafts you save while composing will appear here."}
          {folder === 'trash' && "Deleted emails will appear here."}
        </p>
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
    <div className="bg-background">
      {/* Header row */}
      <div className="flex items-center px-4 py-3 border-b bg-muted/30">
        <div className="email-actions flex items-center gap-3">
          <Checkbox
            checked={allSelected}
            onCheckedChange={(checked) => onSelectAll?.(!!checked)}
            className="h-4 w-4"
          />
          <span className="text-sm text-muted-foreground">
            {selectedEmails.length === 0
              ? `${emails.length} conversations`
              : `${selectedEmails.length} selected`
            }
          </span>
        </div>
      </div>

      {/* Email list */}
      <div className="divide-y divide-border">
        {emails.map((email) => {
          const isSelected = selectedEmails.includes(email.id);
          const starred = isStarred(email);
          const isUnread = !email.read;
          const preview = stripHtml(email.body || email.body_html || '');

          return (
            <div
              key={email.id}
              onClick={(e) => handleEmailClick(email.id, e)}
              className={cn(
                "flex items-center px-4 py-3 hover:shadow-sm transition-all cursor-pointer group",
                isSelected && "bg-accent border-l-4 border-l-primary",
                isUnread ? "bg-background" : "bg-muted/20",
                "border-l-4 border-l-transparent hover:border-l-muted-foreground/30"
              )}
            >
              {/* Selection checkbox */}
              <div className="email-actions flex items-center mr-3">
                <Checkbox
                  checked={isSelected}
                  onCheckedChange={(checked) => onSelectEmail(email.id, !!checked)}
                  className="h-4 w-4"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>

              {/* Star button */}
              <div className="email-actions mr-3">
                <Button
                  variant="ghost"
                  size="sm"
                  className="p-1 h-6 w-6 hover:bg-accent"
                  title={starred ? 'Unstar' : 'Star'}
                  onClick={(e) => handleStarToggle(email, e)}
                >
                  <Star
                    className={cn(
                      "h-4 w-4",
                      starred
                        ? "text-yellow-400 fill-current"
                        : "text-muted-foreground/50 group-hover:text-muted-foreground"
                    )}
                  />
                </Button>
              </div>

              {/* Avatar */}
              <Avatar className="h-8 w-8 mr-3 flex-shrink-0">
                <AvatarFallback className={cn(
                  "text-xs",
                  isUnread ? "bg-primary/10 text-primary font-semibold" : "bg-muted text-muted-foreground"
                )}>
                  {folder === 'sent' || folder === 'drafts'
                    ? getInitials('', email.to?.[0] || '')
                    : getInitials(email.from_name || '', email.from || '')}
                </AvatarFallback>
              </Avatar>

              {/* Sender */}
              <div className="w-40 lg:w-48 flex-shrink-0">
                {folder === 'sent' || folder === 'drafts' ? (
                  <div className={cn(
                    "truncate text-sm",
                    isUnread ? "font-semibold text-foreground" : "font-normal text-muted-foreground"
                  )}>
                    To: {email.to && email.to.length > 0 ? email.to.join(', ') : 'No recipients'}
                  </div>
                ) : (
                  <div className="flex flex-col">
                    <div className={cn(
                      "truncate text-sm",
                      isUnread ? "font-semibold text-foreground" : "font-medium text-foreground"
                    )}>
                      {email.from_name || email.from || 'Unknown Sender'}
                    </div>
                    <div className="truncate text-xs text-muted-foreground">
                      {email.from || 'No email address'}
                    </div>
                  </div>
                )}
              </div>

              {/* Subject and preview */}
              <div className="flex-1 min-w-0 mr-4">
                <div className="flex flex-col gap-0.5">
                  <div className={cn(
                    "text-sm line-clamp-1 flex items-center gap-2",
                    isUnread ? "font-semibold text-foreground" : "font-normal text-foreground/90"
                  )}>
                    {folder === 'drafts' && (
                      <span className="inline-flex items-center rounded-sm bg-amber-500/10 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 text-[10px] font-medium leading-none flex-shrink-0">
                        Draft
                      </span>
                    )}
                    <span className="truncate">{email.subject || '(no subject)'}</span>
                  </div>
                  {preview && (
                    <div className="text-xs text-muted-foreground line-clamp-1">
                      {preview}
                    </div>
                  )}
                </div>
              </div>

              {/* Hover quick actions */}
              <div className="email-actions hidden group-hover:flex items-center gap-1 mr-2 flex-shrink-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="p-1 h-7 w-7"
                      onClick={(e) => handleMarkRead(email, e)}
                    >
                      {email.read
                        ? <Mail className="h-4 w-4 text-muted-foreground" />
                        : <MailOpen className="h-4 w-4 text-muted-foreground" />}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>{email.read ? 'Mark as unread' : 'Mark as read'}</TooltipContent>
                </Tooltip>
                {folder !== 'trash' && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="p-1 h-7 w-7"
                        onClick={(e) => handleTrash(email, e)}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Move to trash</TooltipContent>
                  </Tooltip>
                )}
              </div>

              {/* Attachments indicator */}
              {(email.has_attachments || (email.attachments && email.attachments.length > 0)) && (
                <div className="mr-3 group-hover:hidden">
                  <Paperclip className="h-4 w-4 text-muted-foreground" />
                </div>
              )}

              {/* Date */}
              <div className="text-sm text-muted-foreground w-20 text-right flex-shrink-0 group-hover:hidden">
                {email.date ? formatDate(email.date) : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
    </TooltipProvider>
  );
};

export default EmailList;
